import h from 'hyperscript';
import { intervalToDuration } from 'date-fns';

import { isRequired } from 'cadenza/utils/is-required';
import { TickUnit } from 'cadenza/conditions/model/tick-unit';
import { addTickUnits, differenceInTickUnit } from 'cadenza/conditions/date-time-condition/date-time-condition-utils';

import './slider-steps-config.css';
import i18n from './slider-steps-config.properties';

const COMPONENT_NAME = 'd-slider-steps-config';

export const STEPS_CONFIG_CHANGE = 'change:stepsConfig';

export class SliderStepsConfig extends HTMLElement {

  // Config
  _minTickUnit;
  _rangeStart;
  _rangeEnd;

  // UI
  _timeWindowInput;
  _unitSelect;
  _unitSelectOptions;
  _overlapToggle;
  _overlapInput;
  _content;

  _disabled = false;

  /**
   * @param {import('cadenza/conditions/date-time-condition/date-time-condition').StepsConfig} currentStepsConfig - the current configuration
   * @param {TickUnit} minTickUnit - the smallest unit usable for specifying the step time window
   *   and overlap
   * @param {Date} rangeStart - start of the slider date range
   * @param {Date} rangeEnd - end of the slider date range
   * @param {boolean} disabled
   */
  constructor (
    {
      unit = isRequired(),
      timeWindow = isRequired(),
      overlap = isRequired()
    },
    minTickUnit = isRequired(),
    rangeStart = isRequired(),
    rangeEnd = isRequired(),
    disabled = false
  ) {
    super();
    this._minTickUnit = minTickUnit;
    this._rangeStart = rangeStart;
    this._rangeEnd = rangeEnd;

    const timeWindowLabel = h('.d-slider-steps-config--label', i18n('timeWindow'));
    this._timeWindowInput = this._createTimeWindowInput(timeWindow, unit, rangeStart, rangeEnd);
    const unitLabel = h('.d-slider-steps-config--label', i18n('timeUnit'));
    this._unitSelect = this._createUnitSelect(unit);
    const overlapToggleContainer = this._createOverlapToggle(overlap);
    this._overlapInput = this._createOverlapInput(overlap, timeWindow);
    this._disabled = disabled;

    this._content = [
      timeWindowLabel,
      unitLabel,
      overlapToggleContainer,
      this._timeWindowInput,
      this._unitSelect,
      this._overlapInput
    ];
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME, 'light-bg');
    this.append(...this._content);
    this.disabled = this._disabled;
  }

  _createTimeWindowInput (timeWindow, unit, rangeStart, rangeEnd) {
    const min = 1;
    return h('input.form-control', {
      type: 'number',
      min,
      max: differenceInTickUnit(rangeStart, rangeEnd, unit),
      value: timeWindow,
      onchange: event => {
        clampNumberInputValueBetweenMinAndMaxAndMakeInteger(event.target);
        this._onTimeWindowOrUnitChange();
      }
    });
  }

  _createUnitSelect (unit) {
    const selectableTickUnits = TickUnit.values.slice(0, TickUnit.values.indexOf(this._minTickUnit) + 1);
    const availableUnits = getAvailableUnits(this._getDuration());
    this._unitSelectOptions = selectableTickUnits.map(tickUnit => h('option', {
      value: tickUnit,
      selected: tickUnit === unit,
      disabled: !availableUnits[tickUnit]
    }, i18n(`tickUnit.${tickUnit}`)));
    return h(
      'select.form-control.d-slider-steps-config--unit-select',
      {
        onchange: () => {
          this._adaptTimeWindowToRange(this._time);
          this._onTimeWindowOrUnitChange();
        }
      },
      this._unitSelectOptions
    );
  }

  _createOverlapToggle (initialOverlap) {
    this._overlapToggle = h('d-switch',
      {
        checked: initialOverlap > 0,
        onchange: event => {
          this._overlapInput.disabled = !event.target.checked;
          if (parseInt(this._overlapInput.value) !== 0) {
            this._dispatchStepsConfigChange();
          }
        }
      }
    );
    return h('label.d-slider-steps-config--label.d-form-check',
      this._overlapToggle,
      i18n('overlap')
    );
  }

  _createOverlapInput (overlap, timeWindow) {
    const min = 0;
    const max = timeWindow - 1;
    return h('input.form-control', {
      type: 'number',
      min,
      max,
      disabled: parseInt(overlap) === 0,
      value: overlap,
      onchange: event => {
        clampNumberInputValueBetweenMinAndMaxAndMakeInteger(event.target);
        this._dispatchStepsConfigChange();
      }
    });
  }

  _onTimeWindowOrUnitChange () {
    this._adaptOverlapToTimeWindow();
    this._dispatchStepsConfigChange();
  }

  /**
   * Updates the date range and adapts time window and overlap if necessary.
   *
   * @param {boolean} isStart - Whether to set the date at range-start or -end.
   * @param {Date} date - The new date.
   * @return {null|{unit, timeWindow: number, overlap: number}} - null if the steps config did not change, otherwise the updated steps configuration.
   */
  updateDateRange (isStart, date) {
    if (isStart) {
      this._rangeStart = date;
    } else {
      this._rangeEnd = date;
    }
    return this._adaptTimeWindowAndOverlapToRange();
  }

  /**
   * Sets the current step config in case it has been changed externally to this component.
   *
   * @param {import('cadenza/conditions/date-time-condition/date-time-condition').StepsConfig} stepsConfig
   *  - the new steps config
   */
  setStepsConfig (stepsConfig) {
    this._timeWindowInput.value = stepsConfig.timeWindow;
    for (const option of this._unitSelect.children) {
      option.selected = option.value === stepsConfig.unit;
    }
    this._timeWindowInput.max =
      differenceInTickUnit(this._rangeStart, this._rangeEnd, stepsConfig.unit);
    this._overlapInput.value = stepsConfig.overlap;
    this._overlapInput.max = stepsConfig.timeWindow - 1;
    this._overlapToggle.checked = stepsConfig.overlap > 0;
  }

  _adaptTimeWindowAndOverlapToRange () {
    const availableUnits = getAvailableUnits(this._getDuration());
    // Set now unavailable unit-options to disabled.
    this._unitSelectOptions.forEach((selectOption) => {
      selectOption.disabled = !availableUnits[selectOption.value];
    });
    const timeWindowChanged = this._adaptTimeWindowToRange();
    if (timeWindowChanged) {
      this._adaptOverlapToTimeWindow();
    }
    return timeWindowChanged ? this.getStepsConfig() : null;
  }

  _adaptTimeWindowToRange () {
    // adding one minTickUnit to the end date is for taking into account that the most
    // fine-grained unit of the end is being "filled up" automatically by the backend
    const filledUpRangeEnd = addTickUnits(this._rangeEnd, 1, this._minTickUnit);
    const rangeDiffInTickUnit = differenceInTickUnit(
      this._rangeStart,
      filledUpRangeEnd,
      this._unit);
    if (rangeDiffInTickUnit < 1) { // Unit change necessary
      [ this._timeWindow, this._unit ] =
        timeWindowWithMaxUnit(this._rangeStart, filledUpRangeEnd, this._unit);
      this._timeWindowInput.max = this._timeWindow;
      return true;
    } else {
      this._timeWindowInput.max = rangeDiffInTickUnit;
      if (this._timeWindow > rangeDiffInTickUnit) {
        this._timeWindow = rangeDiffInTickUnit;
        return true;
      }
    }
    return false;
  }

  _adaptOverlapToTimeWindow () {
    const newMaxOverlap = Math.max(this._timeWindow - 1, 0);
    this._overlapInput.max = newMaxOverlap;
    if (this._overlap > newMaxOverlap) {
      this._overlap = newMaxOverlap;
    }
  }

  _dispatchStepsConfigChange () {
    this.dispatchEvent(new CustomEvent(STEPS_CONFIG_CHANGE, {
      detail: {
        stepsConfig: this.getStepsConfig()
      }
    }));
  }

  getStepsConfig () {
    return {
      unit: this._unit,
      timeWindow: this._timeWindow,
      overlap: this._overlapToggle.checked ? this._overlap : 0
    };
  }

  get _unit () {
    return this._unitSelect.value;
  }

  set _unit (value) {
    this._unitSelect.value = value;
  }

  get _timeWindow () {
    return parseInt(this._timeWindowInput.value);
  }

  set _timeWindow (value) {
    this._timeWindowInput.value = value;
  }

  get _overlap () {
    return parseInt(this._overlapInput.value);
  }

  set _overlap (value) {
    this._overlapInput.value = value;
  }

  get disabled () {
    return this._disabled;
  }

  set disabled (value) {
    this._disabled = value;
    this._timeWindowInput.disabled = this._disabled;
    this._unitSelect.disabled = this._disabled;
    this._overlapInput.disabled = this._disabled || !this._overlapToggle.checked;
    this._overlapToggle.disabled = this._disabled;
    Array.from(this.getElementsByClassName('d-slider-steps-config--label')).forEach((label) => {
      label.classList.toggle('disabled', this._disabled);
    });
  }

  _getDuration () {
    // adding one minTickUnit to the end date is for taking into account that the most fine-grained
    // unit of the end is being "filled up" automatically by the backend
    const interval = { start: this._rangeStart, end: addTickUnits(this._rangeEnd, 1, this._minTickUnit) };
    return intervalToDuration(interval);
  }

}

function timeWindowWithMaxUnit (rangeStart, rangeEnd, fallbackUnitForZeroLengthRange) {
  for (const unit of Object.values(TickUnit)) {
    const difference = differenceInTickUnit(rangeStart, rangeEnd, unit);
    if (difference >= 1) {
      return [ difference, unit ];
    }
  }
  return [ 0, fallbackUnitForZeroLengthRange ];
}

function clampNumberInputValueBetweenMinAndMaxAndMakeInteger (numberInput) {
  const value = parseInt(numberInput.value);
  const min = parseInt(numberInput.min);
  const max = parseInt(numberInput.max);
  if (value < min) {
    numberInput.value = min;
  } else if (value > max) {
    numberInput.value = max;
  } else {
    numberInput.value = value;
  }
}

function getAvailableUnits (duration) {
  const largestToSmallestUnit = [ 'years', 'months', 'days', 'hours', 'minutes', 'seconds' ];
  const availableUnits = {};
  largestToSmallestUnit.forEach((unit, index)=>{
    const precedingUnit = index > 0 ? largestToSmallestUnit[index - 1] : null;
    const precedingUnitAvailable = precedingUnit ? availableUnits[precedingUnit] : false;
    availableUnits[unit] = duration[unit] > 0 || precedingUnitAvailable;
  });
  availableUnits.weeks = availableUnits.years || availableUnits.months || duration.days > 6;
  return availableUnits;
}

customElements.define(COMPONENT_NAME, SliderStepsConfig);
